import { isPlatformBrowser } from "@angular/common";
import {
  Component,
  ElementRef,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewEncapsulation
} from "@angular/core";
import { AwsImage } from "@dotcom-frontend/data";
import ResizeObserver from "resize-observer-polyfill";
import { debounceTime, Observable, Subscription } from "rxjs";
import { HtmlTagService } from "../../../services/html-tag/html-tag.service";
import { ImageService } from "../../../services/image/image.service";

@Component({
  selector: "dotcom-frontend-image",
  templateUrl: "./Image.component.html",
  styleUrl: "./Image.component.scss",
  encapsulation: ViewEncapsulation.None,
  standalone: true
})
export class ImageComponent implements OnInit, OnDestroy {
  @Input() data?: any;
  @Input() image?: AwsImage;
  @Input() imageClass = "";

  @Input() loading = "lazy";
  @Input() fetchPriority = "auto";
  @Input() addToPreload = false;

  alt = "";
  horizontalFocus: string | undefined;
  verticalFocus: string | undefined;
  secondaryImage?: AwsImage;
  observer?: Subscription;
  awsImageSM?: string;
  awsImageMD?: string;
  awsImageLG?: string;

  isBrowser = isPlatformBrowser(this.platformId);
  secondaryImageBreakPoints = [];
  requestWidth = 600;
  addedToPreload = false;
  imageStyle = "";
  imageWidths = [1, 400, 600, 800, 1000, 1200, 1400, 2000, 9999999];

  constructor(
    private host: ElementRef,
    private zone: NgZone,
    private imageService: ImageService,
    private htmlTagService: HtmlTagService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {}

  ngOnInit() {
    this.setImageData();
    this.setImageStyle();
    this.setPrimaryImage();
    // we currently don't support this in the CMS
    // this.setSecondaryImage();

    if (this.isBrowser) {
      this.observer = new ResizeObservable(this.host.nativeElement.children[0])
        .pipe(debounceTime(200))
        .subscribe(this.resizeCallback);
    }
  }

  setImageData() {
    // block images
    if (this.image) {
      this.alt = this.image.expanded?.altText;
      this.horizontalFocus = this.image.expanded?.horizontalFocus;
      this.verticalFocus = this.image.expanded?.verticalFocus;
    }
    // content area images
    if (this.data) {
      this.image = this.data.contentLink;
      this.alt = this.data.altText;
      this.horizontalFocus = this.data.horizontalFocus;
      this.verticalFocus = this.data.verticalFocus;
    }
  }

  setImageStyle() {
    const horizontal = this.horizontalFocus || 50;
    const vertical = this.verticalFocus || 50;
    if (this.horizontalFocus || this.verticalFocus) {
      this.imageStyle = `object-position: ${horizontal}% ${vertical}%;`;
    }
  }

  setPrimaryImage() {
    if (this.image) {
      this.awsImageSM = this.imageService.getImageUrl(
        this.image,
        this.requestWidth
      );
      this.awsImageMD = this.imageService.getImageUrl(
        this.image,
        this.requestWidth
      );
      this.awsImageLG = this.imageService.getImageUrl(
        this.image,
        this.requestWidth
      );
      if (this.addToPreload && !this.addedToPreload) {
        this.setImagePreload();
      }
    }
  }

  setImagePreload() {
    this.imageWidths.forEach((width, index) => {
      if (this.imageWidths[index + 1]) {
        this.htmlTagService.addTag({
          tag: "link",
          identifier: `js-hero-image-${this.imageWidths[index + 1]}`,
          rel: "preload",
          as: "image",
          href: this.imageService.getImageUrl(
            this.image,
            this.imageWidths[index + 1]
          ),
          media: `(min-width: ${width}px) and (max-width: ${
            this.imageWidths[index + 1]
          }px)`
        });
      }
    });
    this.addedToPreload = true;
  }

  resizeCallback = (entry: any) => {
    this.zone.run(() => {
      const containerWidth = entry[0].contentRect.width;
      // if we already loaded a large image skip loading a smaller one
      if (containerWidth < this.requestWidth) {
        return;
      }
      switch (true) {
        case containerWidth <= 400:
          this.requestWidth = 400;
          break;
        case 400 < containerWidth && containerWidth <= 600:
          this.requestWidth = 600;
          break;
        case 600 < containerWidth && containerWidth < 800:
          this.requestWidth = 800;
          break;
        case 800 < containerWidth && containerWidth < 1000:
          this.requestWidth = 1000;
          break;
        case 1000 < containerWidth && containerWidth < 1200:
          this.requestWidth = 1200;
          break;
        case 1200 < containerWidth && containerWidth < 1400:
          this.requestWidth = 1400;
          break;
        default:
          this.requestWidth = 2000;
          break;
      }
      this.setPrimaryImage();
      // we currently don't support this in the CMS
      // this.setSecondaryImage();
    });
  };

  ngOnDestroy() {
    if (this.observer && !this.observer.closed) {
      this.observer.unsubscribe();
    }
    if (this.isBrowser && this.addedToPreload) {
      this.imageWidths.forEach((width, index) => {
        if (this.imageWidths[index + 1]) {
          this.htmlTagService.addTag({
            identifier: `js-hero-image-${this.imageWidths[index + 1]}`,
            mode: "remove"
          });
        }
      });
    }
  }
}

export class ResizeObservable extends Observable<ResizeObserverEntry[]> {
  constructor(elem: HTMLElement) {
    super((subscriber) => {
      const ro = new ResizeObserver((entries) => {
        subscriber.next(entries);
      });
      ro.observe(elem);
      return function unsubscribe() {
        ro.unobserve(elem);
        ro.disconnect();
      };
    });
  }
}
